/**
 * Roles, and what each one actually resolves to.
 *
 * The substance is the last column. A role granting `finance.invoice.*` on an
 * installation without Finance grants **nothing** — the API strips it when the
 * principal is built and would refuse the call anyway. A screen that showed the
 * grant without saying so would be describing a capability that does not exist,
 * and somebody would spend an afternoon working out why the holder cannot see
 * invoices.
 *
 * So an inactive grant is labelled, and the label names the remedy: a switch on
 * the Services screen, not an edit to the role.
 */

import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Info, Lock, ShieldCheck } from 'lucide-react';
import {
  Alert,
  ApiError,
  Badge,
  Card,
  CardBody,
  ErrorState,
  LoadingState,
  PageHeader,
  api,
  formatNumber,
} from '@connect/web-shared';

interface Grant {
  resource: string;
  features: string[];
}

interface RoleRow {
  public_id: string;
  name: string;
  description: string | null;
  kind: string;
  is_system: boolean;
  grants: Grant[];
  holder_count: number;
  /** Services this role grants against that are not enabled here. */
  inactive_services: string[];
  created_at: string;
}

export function RolesPage() {
  const roles = useQuery({
    queryKey: ['iam', 'roles'],
    queryFn: () => api.get<RoleRow[]>('/iam/roles'),
  });

  if (roles.isLoading) return <LoadingState label="Reading the roles" />;
  if (roles.isError) {
    return (
      <ErrorState
        message={roles.error instanceof ApiError ? roles.error.message : undefined}
        onRetry={() => void roles.refetch()}
      />
    );
  }

  const rows = roles.data ?? [];

  return (
    <div className="space-y-4">
      <PageHeader
        title="Roles"
        description="A role is a named bundle of capabilities. A deny lives in a policy, because grants can only allow."
        breadcrumbs={[{ label: 'Access' }, { label: 'Roles' }]}
        actions={<Badge tone="accent">{formatNumber(rows.length)} roles</Badge>}
      />

      <Alert tone="info" title="Connect ships these roles and keeps them current">
        A shipped role is replaced on upgrade, so edits to one would be lost. To change what
        somebody can do, give them a different role or attach a policy — a policy is also the only
        way to express a deny, and a deny beats every allow.
      </Alert>

      <div className="grid gap-3 lg:grid-cols-2">
        {rows.map((role) => (
          <Card key={role.public_id}>
            <CardBody className="space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-sm font-semibold text-heading">{role.name}</h3>
                    {role.is_system ? (
                      <Badge tone="neutral">
                        <Lock className="size-2.5" />
                        Shipped
                      </Badge>
                    ) : null}
                  </div>
                  {role.description ? (
                    <p className="text-xs leading-relaxed text-muted">{role.description}</p>
                  ) : null}
                </div>
                <Badge tone={role.holder_count > 0 ? 'accent' : 'neutral'}>
                  <ShieldCheck className="size-2.5" />
                  {formatNumber(role.holder_count)}
                </Badge>
              </div>

              <div className="space-y-1.5">
                <p className="text-2xs font-medium uppercase tracking-wide text-faint">
                  Grants
                </p>
                <div className="flex flex-wrap gap-1">
                  {role.grants.length === 0 ? (
                    <span className="text-2xs text-faint">Nothing. A holder can sign in and read nothing.</span>
                  ) : (
                    role.grants.map((grant, index) => (
                      <span
                        key={`${grant.resource}-${index}`}
                        className="rounded-pill border border-line px-1.5 py-0.5 font-mono text-2xs text-muted"
                        title={`${grant.resource}: ${grant.features.join(', ')}`}
                      >
                        {grant.resource === '*' ? 'everything' : grant.resource}
                        {grant.features.includes('*') ? '' : ` · ${grant.features.length}`}
                      </span>
                    ))
                  )}
                </div>
              </div>

              {/* The load-bearing part. See the header. */}
              {role.inactive_services.length > 0 ? (
                <p className="flex items-start gap-1.5 rounded-control bg-warning-subtle px-2.5 py-1.5 text-2xs leading-relaxed text-warning">
                  <Info className="mt-0.5 size-3 shrink-0" aria-hidden />
                  <span>
                    Part of this role grants access to{' '}
                    <strong>{role.inactive_services.join(', ')}</strong>, which{' '}
                    {role.inactive_services.length === 1 ? 'is' : 'are'} not enabled here — so it
                    currently grants nothing.{' '}
                    <Link to="/settings/services" className="underline underline-offset-2">
                      Services
                    </Link>
                    .
                  </span>
                </p>
              ) : null}
            </CardBody>
          </Card>
        ))}
      </div>
    </div>
  );
}
